export const INSTALL_DISMISSED_KEY = "akongo:pwa-install-dismissed-until";
export const INSTALL_REMINDER_DELAY = 7 * 24 * 60 * 60 * 1000;

/** Keeps the browser's single-use install event across V1 → V2 navigation. */
export function createInstallController(browserWindow, now = Date.now) {
  const listeners = new Set();
  const displayModes = ["standalone", "fullscreen"].map(mode =>
    browserWindow?.matchMedia?.(`(display-mode: ${mode})`)
  ).filter(Boolean);
  const navigator = browserWindow?.navigator || {};
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent || "") ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  let deferredPrompt = null;
  let installed = false;
  let busy = false;
  let dismissedUntil = 0;
  let started = false;
  let snapshot = { mode: null, installed: false, busy: false };

  const readDismissal = () => {
    try {
      const value = Number(browserWindow.localStorage.getItem(INSTALL_DISMISSED_KEY));
      dismissedUntil = Number.isFinite(value) ? value : 0;
    } catch (_) { /* Private browsing must not break the app. */ }
  };
  const publish = () => {
    const standalone = installed || navigator.standalone === true ||
      displayModes.some(query => query.matches);
    const mode = standalone || busy || now() < dismissedUntil
      ? null : deferredPrompt ? "native" : ios ? "ios" : null;
    if (snapshot.mode === mode && snapshot.installed === standalone && snapshot.busy === busy) return;
    snapshot = { mode, installed: standalone, busy };
    listeners.forEach(listener => listener());
  };
  const deferInstall = event => {
    if (typeof event.prompt !== "function") return;
    event.preventDefault();
    deferredPrompt = event;
    publish();
  };
  const markInstalled = () => {
    installed = true;
    deferredPrompt = null;
    publish();
  };
  const refresh = () => { readDismissal(); publish(); };
  const start = () => {
    if (started || !browserWindow) return;
    started = true;
    readDismissal();
    browserWindow.addEventListener("beforeinstallprompt", deferInstall);
    browserWindow.addEventListener("appinstalled", markInstalled);
    browserWindow.addEventListener("storage", refresh);
    browserWindow.addEventListener("focus", refresh);
    displayModes.forEach(query => {
      if (query.addEventListener) query.addEventListener("change", publish);
      else query.addListener?.(publish);
    });
    publish();
  };
  const dismiss = () => {
    dismissedUntil = now() + INSTALL_REMINDER_DELAY;
    try {
      browserWindow.localStorage.setItem(INSTALL_DISMISSED_KEY, String(dismissedUntil));
    } catch (_) { /* The in-memory dismissal still works. */ }
    publish();
  };
  const install = async () => {
    const prompt = deferredPrompt;
    if (!prompt || busy || snapshot.installed) return { status: "unavailable" };
    // Call prompt() directly in the click handler, before any await.
    deferredPrompt = null;
    busy = true;
    publish();
    try {
      await prompt.prompt();
      const choice = await prompt.userChoice;
      dismiss();
      return { status: choice?.outcome === "accepted" ? "accepted" : "dismissed" };
    } catch (_) {
      return { status: "failed" };
    } finally {
      busy = false;
      publish();
    }
  };
  const dispose = () => {
    browserWindow?.removeEventListener("beforeinstallprompt", deferInstall);
    browserWindow?.removeEventListener("appinstalled", markInstalled);
    browserWindow?.removeEventListener("storage", refresh);
    browserWindow?.removeEventListener("focus", refresh);
    displayModes.forEach(query => {
      if (query.removeEventListener) query.removeEventListener("change", publish);
      else query.removeListener?.(publish);
    });
    started = false;
    listeners.clear();
  };
  return {
    start, dispose, dismiss, install,
    getSnapshot: () => snapshot,
    subscribe: listener => { listeners.add(listener); return () => listeners.delete(listener); },
  };
}

export const pwaInstallController = createInstallController(
  typeof window === "undefined" ? null : window
);
